/**
 * ============================================================================
 * DocumentNumberingAdmin — C-12 (drafts/63 — configurable document numbering)
 * Owner: DMS workstream.
 * Route: /admin/numbering
 * Purpose: govern the rule-based numbering schemes that replace the throwaway
 *   {TYPE}-AUTO-{random} doc numbers. One row per document type (SOP, BMR, …)
 *   owning a prefix, optional department segment, separator, and zero-pad
 *   width. The next-sequence counter is system-managed (allocated atomically
 *   at document creation) and is shown read-only here — admins edit the shape,
 *   never the counter, so numbers stay gap-free and auditable.
 *
 *   Mohanad (drafts/63 C-12): "rule-based SOP-QA-001 schemes per type/dept."
 * ============================================================================ */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, CardBody, CardHeader, PageHero } from '@edms/design-system';
import wstyles from '../WebhookAdmin/WebhookAdmin.module.css';
import dialogStyles from '../AuthorWorkbench/NewDocumentForm.module.css';
import styles from './DocumentNumberingAdmin.module.css';
import { ReAuthCeremony } from '../../components/ReAuthCeremony';
import {
  useNumberingSchemesQuery, useUpdateScheme, previewNumber,
  type NumberingScheme,
} from './useDocumentNumbering';

export function DocumentNumberingAdmin() {
  const { t } = useTranslation();
  const schemesQ = useNumberingSchemesQuery();
  const [editing, setEditing] = useState<NumberingScheme | null>(null);

  const items = schemesQ.data?.items ?? [];
  const isEmpty = !schemesQ.isLoading && !schemesQ.isError && items.length === 0;

  return (
    <div className={wstyles.adminScreen}>
      <PageHero
        eyebrow={t('numbering.eyebrow', { defaultValue: 'Administration' })}
        title={t('numbering.title', { defaultValue: 'Document Numbering' })}
        subtitle={t('numbering.subtitle', {
          defaultValue:
            'Rule-based numbering schemes per document type (e.g. SOP-QA-001). The sequence counter is allocated automatically at creation and cannot be edited here.',
        })}
      />

      <Card>
        <CardHeader>
          <h2 className={wstyles.sectionTitle}>
            {t('numbering.list.title', { defaultValue: 'Schemes' })}{' '}
            <span style={{ color: 'var(--color-text-muted)' }}>({items.length})</span>
          </h2>
        </CardHeader>
        <CardBody>
          {schemesQ.isLoading && (
            <div role="status" aria-live="polite" aria-label={t('common.loading', { defaultValue: 'Loading…' })}>
              {[0, 1, 2, 3].map((i) => <div key={i} className={styles.skeletonRow} aria-hidden="true" />)}
            </div>
          )}

          {schemesQ.isError && (
            <div className={styles.errorBanner} role="alert">
              <span>{t('numbering.errors.loadFailed', { defaultValue: 'Could not load numbering schemes.' })}</span>
              <Button variant="secondary" size="sm" onClick={() => schemesQ.refetch()}>
                {t('common.retry', { defaultValue: 'Retry' })}
              </Button>
            </div>
          )}

          {isEmpty && (
            <div className={styles.emptyState}>
              <h3>{t('numbering.empty.title', { defaultValue: 'No numbering schemes configured.' })}</h3>
              <p>{t('numbering.empty.body', { defaultValue: 'Documents fall back to the AUTO pattern until a scheme exists for their type.' })}</p>
            </div>
          )}

          {!schemesQ.isLoading && !schemesQ.isError && items.length > 0 && (
            <table className={wstyles.table}>
              <thead>
                <tr>
                  <th>{t('numbering.col.type', { defaultValue: 'Document type' })}</th>
                  <th>{t('numbering.col.prefix', { defaultValue: 'Prefix' })}</th>
                  <th>{t('numbering.col.dept', { defaultValue: 'Dept segment' })}</th>
                  <th>{t('numbering.col.pad', { defaultValue: 'Pad width' })}</th>
                  <th>{t('numbering.col.next', { defaultValue: 'Next number' })}</th>
                  <th>{t('numbering.col.active', { defaultValue: 'Active' })}</th>
                  <th aria-label={t('common.actions', { defaultValue: 'Actions' })} />
                </tr>
              </thead>
              <tbody>
                {items.map((s) => (
                  <tr key={s.schemeId}>
                    <td><strong>{s.documentType}</strong></td>
                    <td><code>{s.prefix}</code></td>
                    <td>{s.deptSegment ? <code>{s.deptSegment}</code> : <span style={{ color: 'var(--color-text-muted)' }}>—</span>}</td>
                    <td>{s.padWidth}</td>
                    <td><code className={styles.numberChip}>{s.nextNumber}</code></td>
                    <td>
                      <span className={s.active ? styles.pillActive : styles.pillInactive}>
                        {s.active ? t('numbering.active', { defaultValue: 'Active' }) : t('numbering.inactive', { defaultValue: 'Inactive' })}
                      </span>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <Button variant="secondary" size="sm" onClick={() => setEditing(s)}>
                        {t('common.edit', { defaultValue: 'Edit' })}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>

      {editing && (
        <EditSchemeDialog
          scheme={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); schemesQ.refetch(); }}
        />
      )}
    </div>
  );
}

/* ----------------------------------------------------------------------------
 * EditSchemeDialog — edit prefix / dept segment / pad width / active for one
 * scheme, with a live preview of the resulting next number. The sequence
 * counter is shown read-only (system-managed) so admins can never create gaps.
 * --------------------------------------------------------------------------- */
interface EditSchemeDialogProps {
  scheme: NumberingScheme;
  onClose: () => void;
  onSaved: () => void;
}
function EditSchemeDialog({ scheme, onClose, onSaved }: EditSchemeDialogProps) {
  const { t } = useTranslation();
  const updateM = useUpdateScheme(scheme.documentType);
  const [prefix, setPrefix] = useState(scheme.prefix);
  const [deptSegment, setDeptSegment] = useState(scheme.deptSegment ?? '');
  const [padWidth, setPadWidth] = useState(String(scheme.padWidth));
  const [active, setActive] = useState(scheme.active);
  const [error, setError] = useState<string | null>(null);
  // B-5b — a numbering-scheme change is a controlled-config action; require a
  // step-up re-authentication ceremony before it is applied.
  const [reauthOpen, setReauthOpen] = useState(false);

  const padNum = Number(padWidth);
  const previewBad = !prefix.trim() || !Number.isFinite(padNum) || padNum < 1 || padNum > 8;
  const preview = previewBad ? '—' : previewNumber({
    prefix: prefix.trim(), deptSegment: deptSegment.trim() || null,
    separator: scheme.separator, padWidth: padNum, nextSeq: scheme.nextSeq,
  });

  // Validate, then require step-up re-auth (B-5b) before applying the change.
  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!prefix.trim()) {
      setError(t('numbering.errors.prefixRequired', { defaultValue: 'A prefix is required.' }));
      return;
    }
    if (!Number.isFinite(padNum) || padNum < 1 || padNum > 8) {
      setError(t('numbering.errors.padRange', { defaultValue: 'Pad width must be between 1 and 8.' }));
      return;
    }
    setReauthOpen(true);
  }

  async function applyChange() {
    setReauthOpen(false);
    try {
      await updateM.mutateAsync({
        prefix: prefix.trim(),
        deptSegment: deptSegment.trim() ? deptSegment.trim() : null,
        padWidth: padNum,
        active,
      });
      onSaved();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t('numbering.errors.saveFailed', { defaultValue: 'Server rejected the change.' }));
    }
  }

  return (
    <div className={dialogStyles.backdrop} role="presentation" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={dialogStyles.dialog} role="dialog" aria-modal="true" aria-labelledby="dn-edit-title">
        <header className={dialogStyles.header}>
          <h2 id="dn-edit-title" className={dialogStyles.title}>
            {t('numbering.edit.title', { defaultValue: 'Edit numbering scheme' })} · {scheme.documentType}
          </h2>
          <p className={dialogStyles.desc}>
            {t('numbering.edit.subtitle', {
              defaultValue: 'Changes apply to documents created after saving. The sequence counter is system-managed and is not affected.',
            })}
          </p>
        </header>

        <form className={dialogStyles.form} onSubmit={onSubmit} noValidate>
          {error && <div role="alert" className={dialogStyles.errorBanner}>{error}</div>}

          <div className={dialogStyles.field}>
            <label htmlFor="dn-prefix" className={dialogStyles.label}>
              {t('numbering.field.prefix', { defaultValue: 'Prefix' })}
              <span className={dialogStyles.required} aria-hidden="true">*</span>
            </label>
            <input id="dn-prefix" className={dialogStyles.input} value={prefix} maxLength={16}
              onChange={(e) => setPrefix(e.target.value.toUpperCase())} placeholder="SOP" required />
          </div>

          <div className={dialogStyles.field}>
            <label htmlFor="dn-dept" className={dialogStyles.label}>
              {t('numbering.field.dept', { defaultValue: 'Department segment (optional)' })}
            </label>
            <input id="dn-dept" className={dialogStyles.input} value={deptSegment} maxLength={16}
              onChange={(e) => setDeptSegment(e.target.value.toUpperCase())} placeholder="QA" />
          </div>

          <div className={dialogStyles.field}>
            <label htmlFor="dn-pad" className={dialogStyles.label}>
              {t('numbering.field.pad', { defaultValue: 'Zero-pad width (1–8)' })}
              <span className={dialogStyles.required} aria-hidden="true">*</span>
            </label>
            <input id="dn-pad" className={dialogStyles.input} type="number" min={1} max={8} value={padWidth}
              onChange={(e) => setPadWidth(e.target.value)} required />
          </div>

          <div className={dialogStyles.field}>
            <label className={dialogStyles.label} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
              {t('numbering.field.active', { defaultValue: 'Active (allocate numbers for this type)' })}
            </label>
          </div>

          <div className={styles.previewBox} role="note">
            <span className={styles.previewLabel}>{t('numbering.preview', { defaultValue: 'Next number preview' })}</span>
            <code className={styles.previewValue}>{preview}</code>
            <span className={styles.previewSeq}>
              {t('numbering.seqNote', { defaultValue: 'sequence #{{n}} (system-managed)', n: scheme.nextSeq })}
            </span>
          </div>

          <footer className={dialogStyles.footer}>
            <Button type="button" variant="secondary" onClick={onClose} disabled={updateM.isPending}>
              {t('common.cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button type="submit" variant="primary" isLoading={updateM.isPending}>
              {t('common.save', { defaultValue: 'Save' })}
            </Button>
          </footer>
        </form>
      </div>

      {/* B-5b — step-up re-auth before applying the controlled-config change. */}
      <ReAuthCeremony
        open={reauthOpen}
        targetType="NUMBERING_SCHEME"
        targetId={scheme.schemeId}
        meaning={`Edit numbering scheme ${scheme.documentType}`}
        title="Authorise numbering-scheme change"
        onConfirm={() => void applyChange()}
        onCancel={() => setReauthOpen(false)}
      />
    </div>
  );
}
